"use client";

import Link from "next/link";
import {
  SATURN_TRANSIT,
  RASHI_STATUS,
  STATUS_COLORS,
  TRANSIT_TIMELINE,
  type RashiStatus,
} from "./_data";

// ── Saturn Position Card ──────────────────────────────────────────────────────

interface SaturnPositionCardProps {
  saturnDegree: string | null;
  isRetrograde: boolean;
}

export function SaturnPositionCard({ saturnDegree, isRetrograde }: SaturnPositionCardProps) {
  return (
    <div className="card-sacred p-6 mb-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p
            className="text-[12px] uppercase tracking-wider font-semibold mb-1"
            style={{ color: "var(--ish-accent)" }}
          >
            Current Position
          </p>
          <h2
            className="text-2xl font-bold"
            style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
          >
            Shani in {SATURN_TRANSIT.currentRashiHi}
          </h2>
          {saturnDegree && (
            <p className="text-[13px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
              {saturnDegree}° {isRetrograde ? "· Retrograde" : "· Direct"}
            </p>
          )}
        </div>
        <div className="text-right">
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold"
            style={{
              backgroundColor: "rgba(234,179,8,0.12)",
              color: "var(--color-warning-text-dark)",
            }}
          >
            ♄ Aquarius/Pisces
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-[13px]">
        <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--ish-surface-warm)" }}>
          <p
            className="text-[11px] uppercase tracking-wider mb-1"
            style={{ color: "var(--ish-text-faint)" }}
          >
            In Aquarius since
          </p>
          <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
            {SATURN_TRANSIT.entryDate}
          </p>
        </div>
        <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--ish-surface-warm)" }}>
          <p
            className="text-[11px] uppercase tracking-wider mb-1"
            style={{ color: "var(--ish-text-faint)" }}
          >
            Pisces (final entry)
          </p>
          <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
            {SATURN_TRANSIT.finalEntryDate}
          </p>
        </div>
      </div>

      <div
        className="mt-3 p-3 rounded-lg"
        style={{ backgroundColor: "rgba(234,179,8,0.06)", border: "1px solid rgba(234,179,8,0.2)" }}
      >
        <p className="text-[13px]" style={{ color: "var(--ish-text-muted)" }}>
          This is a handoff window: Aquarius lessons are being reviewed while Pisces
          responsibilities start becoming visible. Check the current degree before reading your
          Moon-sign phase.
        </p>
      </div>
    </div>
  );
}

// ── Rashi Picker ──────────────────────────────────────────────────────────────

interface RashiPickerProps {
  selectedRashi: string | null;
  onSelect: (id: string | null) => void;
}

export function RashiPicker({ selectedRashi, onSelect }: RashiPickerProps) {
  const selectedData: RashiStatus | undefined = selectedRashi
    ? RASHI_STATUS.find((r) => r.id === selectedRashi)
    : undefined;

  return (
    <div className="card-sacred p-5 mb-5">
      <p className="text-[14px] font-semibold mb-3" style={{ color: "var(--ish-dark)" }}>
        Check your Moon sign — are you under Shani&apos;s influence?
      </p>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
        {RASHI_STATUS.map((rashi) => {
          const isActive = rashi.status !== "none";
          const isSelected = selectedRashi === rashi.id;
          const colors = STATUS_COLORS[rashi.status];
          return (
            <button
              key={rashi.id}
              onClick={() => onSelect(isSelected ? null : rashi.id)}
              className="flex flex-col items-center gap-0.5 p-2 rounded-lg text-center transition-all"
              style={{
                backgroundColor: isSelected
                  ? colors.badgeBg
                  : isActive
                    ? colors.bg
                    : "var(--ish-surface-warm)",
                border: `1.5px solid ${isSelected ? colors.border : isActive ? colors.border : "var(--ish-border)"}`,
                cursor: "pointer",
              }}
            >
              <span
                className="text-[11px] font-bold leading-tight"
                style={{ color: isActive ? colors.text : "var(--ish-dark)" }}
              >
                {rashi.nameEn}
              </span>
              <span className="text-[10px]" style={{ color: "var(--ish-text-faint)" }}>
                {rashi.nameHi}
              </span>
              {isActive && rashi.id !== "dhanu" && (
                <span
                  className="text-[8px] font-bold mt-0.5 uppercase"
                  style={{ color: colors.badge }}
                >
                  ●
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selectedData && (
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: STATUS_COLORS[selectedData.status].bg,
            border: `1px solid ${STATUS_COLORS[selectedData.status].border}`,
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span
              className="text-[14px] font-bold"
              style={{ color: STATUS_COLORS[selectedData.status].text }}
            >
              {selectedData.nameEn} · {selectedData.nameHi}
            </span>
            <span
              className="text-[11px] font-bold px-2.5 py-1 rounded"
              style={{
                backgroundColor: STATUS_COLORS[selectedData.status].badgeBg,
                color: STATUS_COLORS[selectedData.status].badge,
              }}
            >
              {selectedData.status === "none" ? "NOT ACTIVE" : selectedData.phase.toUpperCase()}
            </span>
          </div>
          <p className="text-[13px]" style={{ color: STATUS_COLORS[selectedData.status].text }}>
            {selectedData.status === "none"
              ? `Sade Sati is not active for ${selectedData.nameEn} right now. ${selectedData.period}.`
              : `${selectedData.nameEn} is under Sade Sati — ${selectedData.phase}. Period: ${selectedData.period}.`}
          </p>
          {selectedData.status !== "none" && (
            <div className="mt-2">
              <Link
                href="/sade-sati"
                className="text-[13px] font-semibold"
                style={{ color: "var(--ish-accent)" }}
              >
                Full Sade Sati analysis + remedies →
              </Link>
            </div>
          )}
        </div>
      )}

      {!selectedRashi && (
        <p className="text-[13px] text-center mt-1" style={{ color: "var(--ish-text-faint)" }}>
          Tap your Moon sign above to check instantly.{" "}
          <Link
            href="/rashi-calculator"
            className="font-semibold"
            style={{ color: "var(--ish-accent)" }}
          >
            Find my Moon sign →
          </Link>
        </p>
      )}
    </div>
  );
}

// ── Affected Rashis List ──────────────────────────────────────────────────────

export function AffectedRashiList() {
  const affectedRashis = RASHI_STATUS.filter((r) => r.status !== "none" && r.id !== "dhanu");
  return (
    <div className="card-sacred p-5 mb-5">
      <h3 className="text-[15px] font-bold mb-4" style={{ color: "var(--ish-dark)" }}>
        Rashis Under Sade Sati (2025–2026)
      </h3>
      <div className="space-y-3">
        {affectedRashis.map((rashi) => {
          const colors = STATUS_COLORS[rashi.status];
          return (
            <div
              key={rashi.id}
              className="flex items-center justify-between p-3 rounded-lg"
              style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
            >
              <div>
                <p className="text-[14px] font-semibold" style={{ color: colors.text }}>
                  {rashi.nameEn} <span className="font-normal text-[13px]">({rashi.nameHi})</span>
                </p>
                <p className="text-[12px]" style={{ color: "var(--ish-text-faint)" }}>
                  {rashi.period}
                </p>
              </div>
              <span
                className="text-[11px] font-bold px-2.5 py-1 rounded flex-shrink-0 ml-3"
                style={{ backgroundColor: colors.badgeBg, color: colors.badge }}
              >
                {rashi.phase.toUpperCase()}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-[12px] mt-3" style={{ color: "var(--ish-text-faint)" }}>
        Sade Sati affects the rashi 12th before, the rashi Saturn is in, and the rashi 2nd after
        Saturn&apos;s position.
      </p>
    </div>
  );
}

// ── Transit Timeline ──────────────────────────────────────────────────────────

export function TransitTimeline() {
  return (
    <div className="card-sacred p-5 mb-6">
      <h3 className="text-[15px] font-bold mb-4" style={{ color: "var(--ish-dark)" }}>
        Saturn Transit Timeline (2020–2030)
      </h3>
      <div className="space-y-2 text-[13px]">
        {TRANSIT_TIMELINE.map(({ date, event, active }) => (
          <div key={date} className="flex items-start gap-3">
            <div
              className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
              style={{ backgroundColor: active ? "var(--ish-accent)" : "var(--ish-border)" }}
            />
            <div>
              <span
                className="font-semibold"
                style={{ color: active ? "var(--ish-dark)" : "var(--ish-text-muted)" }}
              >
                {date}:{" "}
              </span>
              <span style={{ color: active ? "var(--ish-text-muted)" : "var(--ish-text-faint)" }}>
                {event}
              </span>
              {active && (
                <span
                  className="ml-2 text-[11px] font-bold px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: "rgba(var(--ish-accent-rgb),0.12)",
                    color: "var(--ish-accent)",
                  }}
                >
                  Current
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
